import React, { useEffect, useState, useCallback } from 'react';
import { apiService } from '../../../components/Config/api.service';
import Select from "react-select";
import EmployeeSelect from '../../../components/Formulaire/EmployeeSelect';
import { FaTrash } from 'react-icons/fa';
import { notify } from '../../../components/Utils/Notifications';
import { FaRegTrashAlt } from 'react-icons/fa';
import ActionDropdown from '../../../components/Utils/ActionDropdown';
import AsyncSelect from "react-select/async";
const HspForm = ({ setIsDrawerOpen, id, refreshHspList }) => {
    const [formData, setFormData] = useState({
        selectedEmployee: null,
        selectedMonth: new Date().toISOString().slice(0, 7),
        dailyHsp: [{ id: null, date: "", start_time: "", end_time: "", is_holyday: false }],
        overtimeHours: { hs_15: 0, hs_40: 0, hs_60: 0, hs_100: 0 },
        manuallyEditedHours: false
    });

    const { dailyHsp, overtimeHours, selectedMonth, selectedEmployee, manuallyEditedHours } = formData;
    const updateFormData = useCallback((updates) => {
        setFormData(prev => ({ ...prev, ...updates }));
    }, []);

    // helper to round values to N decimals (default 2). Change to 3 for three decimals.
    const round = (value, decimals = 2) => {
        if (value === null || value === undefined || isNaN(Number(value))) return 0;
        return parseFloat(Number(value).toFixed(decimals));
    };

    useEffect(() => {
        if (id) {
            apiService.getElementById('/additionalHours', id)
                .then(response => {
                    const data = response.data.additionalHour;
                    updateFormData({
                        selectedEmployee: {
                            value: data.employee_id,
                            label: data.employee_name 
                        },
                        selectedMonth: data.month.split('/').reverse().join('-'),
                        overtimeHours: {
                            hs_15: round(data.hs_15 || 0),
                            hs_40: round(data.hs_40 || 0),
                            hs_60: round(data.hs_60 || 0),
                            hs_100: round(data.hs_100 || 0),
                        },
                        dailyHsp: data.dailyHsp || [],
                    });
                })
                .catch(error => {
                    console.error('Error fetching additionalHour:', error);
                    notify('Erreur lors de la récupération des données.', '', 3);
                });
        } else {
            updateFormData({
                selectedEmployee: null,
                selectedMonth: new Date().toISOString().slice(0, 7),
                overtimeHours: { hs_15: 0, hs_40: 0, hs_60: 0, hs_100: 0 },
                dailyHsp: [{ id: null, date: "", start_time: "", end_time: "", is_holyday: false }],
                manuallyEditedHours: false
            });
        }
    }, [id, updateFormData]);

    // Calculer les heures supplémentaires en fonction des dates
    useEffect(() => {
        if (!manuallyEditedHours && dailyHsp.length > 0) {
            calculateOvertimeHours();
        }
    }, [dailyHsp, manuallyEditedHours]);

    const calculateOvertimeHours = () => {
        // Variables pour stocker les totaux
        let totalHs15 = 0;
        let totalHs40 = 0;
        let totalHs60 = 0;
        let totalHs100 = 0;

        // Organiser les heures par semaine
        const weeklyHours = {};

        // Traiter chaque entrée quotidienne
        dailyHsp.forEach(day => {
            if (!day.date || !day.start_time || !day.end_time) return;

            const date = new Date(day.date);
            const weekNumber = getWeekNumber(date);

            // Convertir les heures en valeurs numériques
            const startTimeParts = day.start_time.split(':');
            const endTimeParts = day.end_time.split(':');

            if (startTimeParts.length < 2 || endTimeParts.length < 2) return;

            const startTime = parseFloat(startTimeParts[0]) + parseFloat(startTimeParts[1]) / 60;
            const endTime = parseFloat(endTimeParts[0]) + parseFloat(endTimeParts[1]) / 60;

            // Ajuster l'heure de fin si elle est tôt le matin suivant
            const endTimeAdjusted = endTime <= 5 && startTime > 5 ? endTime + 24 : endTime;

            // Calculer les heures de jour et de nuit
            const totalHoursDay = (startTime < 22 && endTimeAdjusted > 22) ? Math.max(0, 22 - startTime) : 0;
            const totalHoursNight = (startTime < 22 && endTimeAdjusted > 22) ? Math.max(0, endTimeAdjusted - 22) : 0;
            const totalHours = (totalHoursDay === 0 && totalHoursNight === 0) ? endTimeAdjusted - startTime : 0;

            // Déterminer si c'est le weekend ou la nuit
            const isWeekend = date.getDay() === 0 || date.getDay() === 6;
            const isHoliday = day.is_holyday;
            const isNight = startTime >= 22 || endTimeAdjusted <= 5;

            // Calculer les heures supplémentaires selon les règles
            let dayHs1540 = 0;
            let dayHs60 = 0;
            let dayHs100 = 0;

            if (totalHours > 0) {
                if (isWeekend || isHoliday) {
                    dayHs100 = isNight ? totalHours : 0;
                    dayHs60 = isNight ? 0 : totalHours;
                } else if (isNight) {
                    dayHs60 = totalHours;
                } else {
                    dayHs1540 = totalHours;
                }
            } else if (totalHoursDay > 0 && totalHoursNight > 0) {
                if (isWeekend || isHoliday) {
                    dayHs100 = totalHoursNight;
                    dayHs60 = totalHoursDay;
                } else {
                    dayHs60 = totalHoursNight;
                    dayHs1540 = totalHoursDay;
                }
            }

            // Accumuler les heures par semaine pour 15% vs 40%
            if (dayHs1540 > 0) {
                weeklyHours[weekNumber] = (weeklyHours[weekNumber] || 0) + dayHs1540;
            }

            // Additionner les 60% et 100% directement
            totalHs60 += dayHs60;
            totalHs100 += dayHs100;
        });

        // Calculer les heures 15% et 40% en fonction du total hebdomadaire
        Object.values(weeklyHours).forEach(weekHours => {
            if (weekHours > 8) {
                totalHs15 += 8;
                totalHs40 += weekHours - 8;
            } else {
                totalHs15 += weekHours;
            }
        });

        // Mettre à jour les heures supplémentaires calculées
        updateFormData({
            overtimeHours: {
                hs_15: round(totalHs15),
                hs_40: round(totalHs40),
                hs_60: round(totalHs60),
                hs_100: round(totalHs100),
            }
        });
    };

    // Fonction pour obtenir le numéro de semaine d'une date
    const getWeekNumber = (date) => {
        const d = new Date(date);
        d.setHours(0, 0, 0, 0);
        d.setDate(d.getDate() + 3 - (d.getDay() + 6) % 7);
        const week1 = new Date(d.getFullYear(), 0, 4);
        return 1 + Math.round(((d - week1) / 86400000 - 3 + (week1.getDay() + 6) % 7) / 7);
    };

    // Gérer le changement manuel d'heures supplémentaires
    const handleOvertimeHoursChange = (field, value) => {
        // ensure numeric and rounded (use 2 decimals by default; pass 3 if needed)
        const numeric = parseFloat(value) || 0;
        const rounded = round(numeric, 2); // change 2 -> 3 to store 3 decimals
        // Marquer que les heures ont été éditées manuellement
        updateFormData({
            manuallyEditedHours: true,
            overtimeHours: {
                ...overtimeHours,
                [field]: rounded,
            }
        });
    };

    // Gérer la modification des entrées quotidiennes d'heures supplémentaires
    const handleDailyHspChange = (index, field, value) => {
        const updated = [...dailyHsp];
        updated[index][field] = value;

        updateFormData({
            dailyHsp: updated,
            manuallyEditedHours: false // Réinitialiser si nous modifions les dates
        });
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        const submissionData = {
            ...formData,
            employee_name: selectedEmployee ? selectedEmployee.label : "",
            selectedEmployee: selectedEmployee ? selectedEmployee.value : null,
            selectedMonth: formData.selectedMonth.split('-').reverse().join('/')
        };

        const apiCall = id
            ? apiService.updateElementById('/additionalHours', id, submissionData)
            : apiService.saveElement('/additionalHour', submissionData);

        apiCall
            .then(response => {
                console.log(id ? 'Update Response:' : 'Create Response:', response.data);
                notify('Enregistrement réussi', '', 2);
                setIsDrawerOpen(false);
                refreshHspList();
            })
            .catch(error => {
                console.error(id ? 'Error updating additionalHour:' : 'Error creating additionalHour:', error);
                notify(`Erreur lors de ${id ? 'la mise à jour' : "l'enregistrement"} des heures supplémentaires.`, '', 3);
            });
    };

    const addNewLine = () => {
        updateFormData({
            dailyHsp: [...dailyHsp, { id: null, date: "", start_time: "", end_time: "", is_holyday: false }]
        });
    };

    const removeLine = (index) => {
        updateFormData({
            dailyHsp: dailyHsp.filter((_, i) => i !== index)
        });
    };

    const handleDelete = async () => {
            if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette absence?')) {
                return;
            }
        
            try {
                const deletedFormData = {
                    active: false,
                };
                apiService.updateElementById(`/additionalHours`, id, deletedFormData)
                    .then(response => {
                        notify('Suppression réussi', '', 2);
                        setIsDrawerOpen(false);
                        refreshHspList();
                    })
            } catch (error) {
                console.error('Error deleting additionalHours:', error);
                notify('Erreur lors de la suppression.', '', 3);
            }
        };
    const [loading, setLoading] = useState(false);
        
    const dropdownActions = [
            {
                label: 'Supprimer',
                onClick: handleDelete,
                disabled: loading || !id,
                color: 'text-red-500',
                icon: <FaRegTrashAlt size={16} />
            }
        ]

    return (
        <div className="p-6 h-screen bg-white text-base-content overflow-y-auto">
            <div className="flex justify-between items-center">
                    <h2 className="text-lg font-semibold mb-4">{id ? "Modifier Heure Supplementaire" : "Ajouter Heure Supplementaire"}</h2>
                    {id && (
                        <ActionDropdown actions={dropdownActions} />
                    )}
                </div>
            <form onSubmit={handleSubmit}>
                <div className="mb-4">
                    <label className="block text-sm font-medium mb-1">Nom de l'employé</label>
                    <EmployeeSelect
                        value={selectedEmployee}
                        onChange={option => updateFormData({ selectedEmployee: option })}
                        placeholder="Choisir l'employé"
                    />
                </div>
                <div className="mb-4">
                    <label className="block text-sm font-medium mb-1">Mois de</label>
                    <input
                        type="month"
                        id="month"
                        name="month"
                        className="input input-bordered w-full"
                        value={selectedMonth}
                        onChange={(e) => updateFormData({ selectedMonth: e.target.value })}
                    />
                </div>

                <div className="text-sm text-gray-600 mb-6">
                    Vous pouvez paramétrer le versement automatique d'une prime de vacances à vos salariés en vous rendant dans les paramètres avancés de votre entreprise.
                </div>

                {/* Mode indicator */}
                <div className="mb-4 flex items-center">
                    <span className="text-sm font-medium mr-2">Mode actuel :</span>
                    <span className="badge badge-primary">{manuallyEditedHours ? "Par Heures" : "Par Dates"}</span>
                    {manuallyEditedHours && (
                        <button
                            type="button"
                            className="ml-4 text-blue-600 text-sm hover:underline"
                            onClick={() => updateFormData({ manuallyEditedHours: false })}
                        >
                            Revenir au mode par dates
                        </button>
                    )}
                </div>

                {/* Overtime Summary */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 bg-yellow-50 p-4 rounded-md mb-6">
                    {[
                        { rate: "15%", field: "hs_15" },
                        { rate: "40%", field: "hs_40" },
                        { rate: "60%", field: "hs_60" },
                        { rate: "100%", field: "hs_100" }
                    ].map(({ rate, field }) => (
                        <div key={rate}>
                            <div className="text-sm font-semibold">
                                Nombre d'heure supplémentaire {rate}
                            </div>
                            <input
                                type="number"
                                className="input w-full mt-2"
                                id={field}
                                name={field}
                                placeholder="0"
                                value={overtimeHours[field]}
                                onChange={(e) => handleOvertimeHoursChange(field, parseFloat(e.target.value) || 0)}
                            />
                        </div>
                    ))}
                </div>

                {/* Dates Lines - Disabled when manually edited hours */}
                <div className={`mb-6 ${manuallyEditedHours ? "opacity-50 pointer-events-none" : ""}`}>
                    <h3 className="text-lg font-medium mb-2">Détail des dates</h3>
                    <table className="table text-sm ">
                        <thead>
                            <tr>
                                <th>Date</th>
                                <th>Heure de début</th>
                                <th>Heure de fin</th>
                                <th>Fériés / Repos</th>
                            </tr>
                        </thead>
                        <tbody>
                            {dailyHsp.map((line, index) => (
                                <tr key={index} >
                                    <td style={{ width: "30%" }}>
                                        <input
                                            type="date"
                                            className="input input-bordered w-full text-sm"
                                            value={line.date}
                                            onChange={(e) => handleDailyHspChange(index, 'date', e.target.value)}
                                        />
                                    </td>
                                    <td>
                                        <input
                                            type="time"
                                            className="input input-bordered w-full text-sm"
                                            value={line.start_time}
                                            onChange={(e) => handleDailyHspChange(index, 'start_time', e.target.value)}
                                        />
                                    </td>
                                    <td>
                                        <input
                                            type="time"
                                            className="input input-bordered w-full text-sm"
                                            value={line.end_time}
                                            onChange={(e) => handleDailyHspChange(index, 'end_time', e.target.value)}
                                        />
                                    </td>
                                    <td>
                                        <div className="flex justify-center content-center">
                                            <input
                                                type="checkbox"
                                                className="toggle toggle-success text-sm mr-4"
                                                checked={line.is_holyday}
                                                onChange={(e) => handleDailyHspChange(index, 'is_holyday', e.target.checked)}
                                            />
                                            <button type="button" onClick={() => removeLine(index)}>
                                                <FaTrash color='red' />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>

                    <button
                        type="button"
                        className="mt-4 text-blue-600 hover:underline"
                        onClick={addNewLine}
                    >
                        + Ajouter une ligne
                    </button>
                </div>

                {/* Form Actions */}
                <div className="flex justify-end gap-4">
                    <button
                        type="button"
                        className="btn btn-outline"
                        onClick={() => setIsDrawerOpen(false)}
                    >
                        Annuler
                    </button>
                    <button type="submit" className="btn btn-outline bg-[--color-primary] text-white">
                        {id ? "Mettre à jour" : "Ajouter"}
                    </button>
                </div>
            </form>
        </div>
    );
};

export default HspForm;